import type { KioskDevice, KioskDeviceStatus } from '../../api/kioskDevices';

/**
 * Pure helpers for the Kiosk Devices page (TAS-585). No React, no clock reads —
 * every time-dependent function takes `now` (epoch ms) so the tests can pin it.
 *
 * Rules from the design handoff (docs/featureBriefs/design_handoff_kiosk_devices):
 *  - Stale = an ACTIVE device whose lastSeenAt is 24 h or more ago (enrolledAt stands
 *    in until its first request). Not enrolled and deactivated devices are never stale.
 *  - The green "live" dot shows for an active device seen in the last 10 minutes.
 *  - Sort: Active, then Not enrolled, then Deactivated; A–Z by name within each.
 */

export const MINUTE_MS = 60_000;
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

export const STALE_AFTER_MS = DAY_MS;
export const FRESH_WITHIN_MS = 10 * MINUTE_MS;
/** The countdown bar turns amber under this. */
export const CODE_LOW_MS = 3 * MINUTE_MS;

/** UI limits. The API accepts names up to 100 chars; the handoff caps the input at 60. */
export const NAME_MAX = 60;
export const NOTE_MAX = 500;

export type StatusFilter = 'all' | 'active' | 'stale' | 'not_enrolled' | 'deactivated';

export const FILTER_LABEL: Record<StatusFilter, string> = {
  all: 'All statuses',
  active: 'Active',
  stale: 'Active · not seen 24h+',
  not_enrolled: 'Not enrolled',
  deactivated: 'Deactivated',
};

export const STATUS_LABEL: Record<KioskDeviceStatus, string> = {
  active: 'Active',
  not_enrolled: 'Not enrolled',
  deactivated: 'Deactivated',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DIGIT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

/** Epoch ms for an ISO string, or null for null / unparseable. */
export function ts(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/* ---------------------------------------------------------------- status */

export function isStale(d: Pick<KioskDevice, 'status' | 'lastSeenAt' | 'enrolledAt'>, now: number): boolean {
  if (d.status !== 'active') return false;
  // A just-enrolled tablet may not have made its first request yet; measure from the
  // enrollment until it does, so it doesn't flash "not seen 24h+" on the success screen.
  const seen = ts(d.lastSeenAt) ?? ts(d.enrolledAt);
  return seen != null && now - seen >= STALE_AFTER_MS;
}

export function isFresh(d: Pick<KioskDevice, 'status' | 'lastSeenAt'>, now: number): boolean {
  if (d.status !== 'active') return false;
  const seen = ts(d.lastSeenAt);
  return seen != null && now - seen < FRESH_WITHIN_MS;
}

/** A pending code that has not yet expired. */
export function hasLiveCode(d: Pick<KioskDevice, 'pendingCode'>, now: number): boolean {
  const exp = ts(d.pendingCode?.expiresAt);
  return exp != null && exp > now;
}

/** Milliseconds until the device's pending code expires (0 when none / expired). */
export function codeMsLeft(d: Pick<KioskDevice, 'pendingCode'>, now: number): number {
  const exp = ts(d.pendingCode?.expiresAt);
  return exp == null ? 0 : Math.max(0, exp - now);
}

/**
 * Has the device enrolled with the code issued at `issuedAt`?
 *
 * Status alone is not enough on a re-enroll: the device is already 'active' (the
 * old tablet keeps working until the new one redeems the code), so success also
 * requires an enrolledAt at or after the code was issued. Both timestamps come
 * from the server, so client clock skew does not enter into it.
 */
export function enrolledSince(d: Pick<KioskDevice, 'status' | 'enrolledAt'>, issuedAt: string): boolean {
  if (d.status !== 'active') return false;
  const enrolled = ts(d.enrolledAt);
  const issued = ts(issuedAt);
  if (enrolled == null || issued == null) return false;
  return enrolled >= issued;
}

/**
 * Was the code issued at `issuedAt` replaced by a newer one (someone else pressed
 * Generate in another window)? Only a pendingCode with a DIFFERENT, later issuedAt
 * counts — a missing pendingCode is ambiguous (consumed or expired) and is decided
 * by enrolledSince / the countdown instead.
 */
export function codeSuperseded(d: Pick<KioskDevice, 'pendingCode'>, issuedAt: string): boolean {
  const theirs = ts(d.pendingCode?.issuedAt);
  const ours = ts(issuedAt);
  return theirs != null && ours != null && theirs > ours;
}

export interface StatusCounts {
  active: number;
  not_enrolled: number;
  deactivated: number;
  stale: number;
}

export function countStatuses(devices: KioskDevice[], now: number): StatusCounts {
  const c: StatusCounts = { active: 0, not_enrolled: 0, deactivated: 0, stale: 0 };
  for (const d of devices) {
    c[d.status]++;
    if (isStale(d, now)) c.stale++;
  }
  return c;
}

/* ---------------------------------------------------------------- list */

const STATUS_ORDER: Record<KioskDeviceStatus, number> = { active: 0, not_enrolled: 1, deactivated: 2 };

export function sortDevices(devices: KioskDevice[]): KioskDevice[] {
  return [...devices].sort(
    (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
      || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

export function matchesFilter(d: KioskDevice, filter: StatusFilter, now: number): boolean {
  if (filter === 'all') return true;
  if (filter === 'stale') return isStale(d, now);
  return d.status === filter;
}

/** Case-insensitive substring search over name + note. */
export function matchesSearch(d: Pick<KioskDevice, 'name' | 'note'>, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return `${d.name} ${d.note ?? ''}`.toLowerCase().includes(q);
}

export function visibleDevices(devices: KioskDevice[], filter: StatusFilter, query: string, now: number): KioskDevice[] {
  return sortDevices(devices.filter((d) => matchesFilter(d, filter, now) && matchesSearch(d, query)));
}

/* ---------------------------------------------------------------- formatting */

/** "4:07" — minutes:seconds, rounding seconds up so it reads 0:01 until it is truly 0. */
export function mmss(ms: number): string {
  const total = Math.ceil(Math.max(0, ms) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

/** "just now" · "2 min ago" · "3 h ago" · "1 day ago" · "2 days ago". */
export function relTime(iso: string | null | undefined, now: number): string {
  const t = ts(iso);
  if (t == null) return '';
  const m = Math.floor((now - t) / MINUTE_MS);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const days = Math.floor(h / 24);
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

/** "1:46 PM" in the browser's local time. */
export function clockTime(iso: string | null | undefined): string {
  const t = ts(iso);
  if (t == null) return '';
  const d = new Date(t);
  const h = d.getHours();
  const m = d.getMinutes();
  return `${(h % 12) || 12}:${m < 10 ? '0' : ''}${m} ${h < 12 ? 'AM' : 'PM'}`;
}

/** "Oct 3" */
export function shortDate(iso: string | null | undefined): string {
  const t = ts(iso);
  if (t == null) return '';
  const d = new Date(t);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Oct 2, 2026" */
export function longDate(iso: string | null | undefined): string {
  const t = ts(iso);
  if (t == null) return '';
  const d = new Date(t);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function sameLocalDay(a: number, b: number): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

/** "Today 1:46 PM" for today's calendar date, else "Oct 3, 11:48 AM". */
export function absSeen(iso: string | null | undefined, now: number): string {
  const t = ts(iso);
  if (t == null) return '';
  if (sameLocalDay(t, now)) return `Today ${clockTime(iso)}`;
  return `${shortDate(iso)}, ${clockTime(iso)}`;
}

/** "483 920" / "483920" → ["483", "920"]. Non-digits are dropped. */
export function codeGroups(code: string): [string, string] {
  const digits = (code || '').replace(/\D/g, '');
  return [digits.slice(0, 3), digits.slice(3)];
}

/** The digits only, for the clipboard: "483 920" → "483920". */
export function codeDigits(code: string): string {
  return (code || '').replace(/\D/g, '');
}

/** "four-eight-three · nine-two-zero" — for reading the code over the phone. */
export function spokenCode(code: string): string {
  const [a, b] = codeGroups(code);
  const say = (g: string) => g.split('').map((c) => DIGIT_WORDS[Number(c)]).join('-');
  return b ? `${say(a)} · ${say(b)}` : say(a);
}

/** "38 punches" / "1 punch". */
export function punchesLabel(n: number): string {
  return `${n} punch${n === 1 ? '' : 'es'}`;
}

/* ---------------------------------------------------------------- validation */

/**
 * Inline validation for the Name field. Returns the error copy, or null when OK.
 *
 * Uniqueness mirrors the API's filtered unique index (UX_KioskDevice_Name_Active):
 * names are unique, case-insensitively, among ACTIVE (not deactivated) devices. A
 * deactivated device's name is free to reuse, and renaming a deactivated device is
 * not checked here — the server is the authority either way (409 field:"name").
 *
 * @param editing the device being edited, or null when adding.
 */
export function validateDeviceName(
  rawName: string,
  devices: Pick<KioskDevice, 'kioskDeviceID' | 'name' | 'status'>[],
  editing: Pick<KioskDevice, 'kioskDeviceID' | 'status'> | null,
): string | null {
  const name = rawName.trim();
  if (!name) return 'Give the device a name.';
  if (name.length > NAME_MAX) return `Keep the name to ${NAME_MAX} characters or fewer.`;
  if (editing && editing.status === 'deactivated') return null;
  const lower = name.toLowerCase();
  const clash = devices.some((d) =>
    d.status !== 'deactivated'
    && (!editing || d.kioskDeviceID !== editing.kioskDeviceID)
    && d.name.trim().toLowerCase() === lower
  );
  return clash ? nameTakenMessage(name) : null;
}

export function nameTakenMessage(name: string): string {
  return `There’s already a device named “${name.trim()}”. Names must be unique.`;
}
